// services/ModuleService.ts

import { moduleApi } from '@/lib/api/moduleApi';
import {
    Module,
    Case,
    ModuleWithCases,
    ModuleStep,
    ModuleFullResponse,
    CandidateModuleProgress,
    SubmitStepResultRequest,
    StepResultResponse,
    IntroStatus,
    CreateModuleRequest,
    UpdateModuleRequest,
    CreateCaseRequest,
    UpdateCaseRequest,
    PaginationRequest,
    UIModule,
    convertModuleToUIModule
} from '@/lib/models/module';
export class ModuleService {

    // ========================================
    // COURSE OPERATIONS
    // ========================================

    /**
     * Get all modules with optional pagination
     */
    static async getModules(pagination?: PaginationRequest): Promise<Module[]> {
        try {
            // Org-scoped endpoint (global + the user's org). #247: never fall back
            // to an unscoped list — module reads require auth + scope now.
            return await moduleApi.getMyModules(pagination);
        } catch (error) {
            console.error('Error in ModuleService.getModules:', error);
            return [];
        }
    }

    /**
     * Get modules formatted for UI consumption with proper category mapping
     */
    static async getModulesForUI(pagination?: PaginationRequest): Promise<UIModule[]> {
        try {
            const modules = await this.getModules(pagination);
            const uiModules: UIModule[] = [];

            for (const module of modules) {
                try {
                    // Get cases for this module to extract category from focus_area
                    const cases = await this.getCasesForModule(module.id);
                    const category = cases.length > 0 ? cases[0].focus_area : 'General';

                    const uiModule = convertModuleToUIModule(module, cases);
                    uiModule.category = category; // Override the default category

                    uiModules.push(uiModule);
                } catch (error) {
                    console.error(`Error getting cases for module ${module.id}:`, error);
                    // Still add the module with default category
                    uiModules.push(convertModuleToUIModule(module));
                }
            }

            return uiModules;
        } catch (error) {
            console.error('Error in ModuleService.getModulesForUI:', error);
            return [];
        }
    }

    /**
     * Get a specific module by ID
     */
    static async getModuleById(moduleId: string): Promise<Module | null> {
        try {
            return await moduleApi.getModuleById(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.getModuleById:', error);
            return null;
        }
    }

    /**
     * Get a module with all its cases embedded
     */
    static async getModuleWithCases(moduleId: string): Promise<ModuleWithCases | null> {
        try {
            return await moduleApi.getModuleWithCases(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.getModuleWithCases:', error);
            return null;
        }
    }

    /**
     * Get a module with cases formatted for UI
     */
    static async getModuleWithCasesForUI(moduleId: string): Promise<UIModule | null> {
        try {
            const moduleWithCases = await this.getModuleWithCases(moduleId);
            if (!moduleWithCases) return null;

            return convertModuleToUIModule(moduleWithCases, moduleWithCases.cases);
        } catch (error) {
            console.error('Error in ModuleService.getModuleWithCasesForUI:', error);
            return null;
        }
    }

    /**
     * Create a new module
     */
    static async createModule(moduleData: CreateModuleRequest): Promise<Module | null> {
        try {
            return await moduleApi.createModule(moduleData);
        } catch (error) {
            console.error('Error in ModuleService.createModule:', error);
            throw error;
        }
    }

    /**
     * Update an existing module
     */
    static async updateModule(moduleId: string, moduleData: UpdateModuleRequest): Promise<Module | null> {
        try {
            return await moduleApi.updateModule(moduleId, moduleData);
        } catch (error) {
            console.error('Error in ModuleService.updateModule:', error);
            throw error;
        }
    }

    /**
     * Delete a module
     */
    static async deleteModule(moduleId: string): Promise<void> {
        try {
            await moduleApi.deleteModule(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.deleteModule:', error);
            throw error;
        }
    }

    // ========================================
    // CASE OPERATIONS
    // ========================================

    /**
     * Get all cases with optional pagination
     */
    static async getCases(pagination?: PaginationRequest): Promise<Case[]> {
        try {
            return await moduleApi.getCases(pagination);
        } catch (error) {
            console.error('Error in ModuleService.getCases:', error);
            return [];
        }
    }

    /**
     * Get a specific case by ID
     */
    static async getCaseById(caseId: string): Promise<Case | null> {
        try {
            return await moduleApi.getCaseById(caseId);
        } catch (error) {
            console.error('Error in ModuleService.getCaseById:', error);
            return null;
        }
    }

    /**
     * Get all cases for a specific module
     */
    static async getCasesForModule(moduleId: string): Promise<Case[]> {
        try {
            return await moduleApi.getCasesForModule(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.getCasesForModule:', error);
            return [];
        }
    }

    /**
     * Create a new case
     */
    static async createCase(caseData: CreateCaseRequest): Promise<Case | null> {
        try {
            return await moduleApi.createCase(caseData);
        } catch (error) {
            console.error('Error in ModuleService.createCase:', error);
            throw error;
        }
    }

    /**
     * Update an existing case
     */
    static async updateCase(caseId: string, caseData: UpdateCaseRequest): Promise<Case | null> {
        try {
            return await moduleApi.updateCase(caseId, caseData);
        } catch (error) {
            console.error('Error in ModuleService.updateCase:', error);
            throw error;
        }
    }

    /**
     * Delete a case
     */
    static async deleteCase(caseId: string): Promise<void> {
        try {
            await moduleApi.deleteCase(caseId);
        } catch (error) {
            console.error('Error in ModuleService.deleteCase:', error);
            throw error;
        }
    }

    // ========================================
    // UTILITY METHODS
    // ========================================

    /**
     * Get module categories (extracted from focus areas of cases)
     */
    static async getModuleCategories(): Promise<string[]> {
        try {
            const modules = await this.getModules();
            const categories = new Set<string>();

            for (const module of modules) {
                try {
                    const cases = await this.getCasesForModule(module.id);
                    cases.forEach(case_ => {
                        if (case_.focus_area) {
                            categories.add(case_.focus_area);
                        }
                    });
                } catch (error) {
                    console.error(`Error getting cases for module ${module.id}:`, error);
                }
            }

            return Array.from(categories);
        } catch (error) {
            console.error('Error in ModuleService.getModuleCategories:', error);
            return ['General', 'Technical', 'Business', 'Design']; // Default categories
        }
    }

    /**
     * Search modules by name or description
     */
    static async searchModules(query: string, pagination?: PaginationRequest): Promise<Module[]> {
        try {
            const allModules = await this.getModules(pagination);

            if (!query.trim()) return allModules;

            const searchTerm = query.toLowerCase();
            return allModules.filter(module =>
                module.module_name.toLowerCase().includes(searchTerm) ||
                module.module_description.toLowerCase().includes(searchTerm)
            );
        } catch (error) {
            console.error('Error in ModuleService.searchModules:', error);
            return [];
        }
    }

    /**
     * Get ongoing modules (this would need user progress data integration)
     * For now, returns empty array as we don't have user progress tracking yet
     */
    static async getOngoingModules(): Promise<UIModule[]> {
        // TODO: Implement this when user progress tracking is added
        // This would require joining module data with user progress data
        console.log('getOngoingModules: User progress tracking not implemented yet');
        return [];
    }

    /**
     * Get available modules (all modules formatted for UI)
     */
    static async getAvailableModules(): Promise<UIModule[]> {
        return this.getModulesForUI();
    }

    // ========================================
    // STEP & PROGRESS OPERATIONS
    // ========================================

    static async getModuleSteps(moduleId: string): Promise<ModuleStep[]> {
        try {
            return await moduleApi.getModuleSteps(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.getModuleSteps:', error);
            return [];
        }
    }

    static async getModuleFull(moduleId: string): Promise<ModuleFullResponse | null> {
        try {
            return await moduleApi.getModuleFull(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.getModuleFull:', error);
            return null;
        }
    }

    static async getModuleProgress(moduleId: string): Promise<CandidateModuleProgress | null> {
        try {
            return await moduleApi.getModuleProgress(moduleId);
        } catch (error) {
            console.error('Error in ModuleService.getModuleProgress:', error);
            return null;
        }
    }

    static async getMyProgress(): Promise<CandidateModuleProgress[]> {
        try {
            return await moduleApi.getMyProgress();
        } catch (error) {
            console.error('Error in ModuleService.getMyProgress:', error);
            return [];
        }
    }

    static async submitStepResult(moduleId: string, request: SubmitStepResultRequest): Promise<StepResultResponse> {
        return moduleApi.submitStepResult(moduleId, request);
    }

    static async getIntroStatus(): Promise<IntroStatus> {
        try {
            return await moduleApi.getIntroStatus();
        } catch (error) {
            console.error('Error in ModuleService.getIntroStatus:', error);
            return { intro_completed: false };
        }
    }
}